import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export type ScopingStage =
  | 'partner_briefing'
  | 'services_positioned'
  | 'scoping_workshop'
  | 'customer_review'
  | 'customer_approval'
  | 'po_sent'
  | 'in_delivery';

export type MobilisationStage =
  | 'internal_planning'
  | 'sales_handover'
  | 'customer_kickoff'
  | 'delivery_start';

// Child project summary
export type RagStatus = 'green' | 'amber' | 'red' | 'na';

export interface ProjectSummary {
  id: string;
  name: string;
  currentScopingStage: ScopingStage | null;
  status: 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'archived';
  totalDays: number;
  totalCost: number;
  currency?: 'GBP' | 'USD' | 'EUR';
  healthStatus?: RagStatus;
  healthNotes?: string | null;
  scopingStages?: ScopingStageInfo[];
}

export interface Opportunity {
  id: string;
  customerId: string | null;
  customerName: string;
  title: string;
  description: string | null;
  salesOrg: string | null;
  accountDirectorId: string | null;
  accountDirectorName: string | null;
  scopingLeadId: string | null;
  scopingLeadName: string | null;
  deliveryLeadId: string | null;
  deliveryLeadName: string | null;
  engagementStatus: 'pipeline' | 'in_delivery';
  currentScopingStage: ScopingStage | null;
  currentMobilisationStage: MobilisationStage | null;
  servicesValue: number | null;
  currency: 'GBP' | 'USD' | 'EUR';
  funding: string | null;
  fundingTypeId: string | null;
  fundingTypeName: string | null;
  serviceClass: string | null;
  contractStartDate: string | null;
  servicesExpiryDate: string | null;
  customerRelationshipStatus: RagStatus | null;
  customerRelationshipNotes: string | null;
  scopingStatus: RagStatus | null;
  scopingNotes: string | null;
  customerReadinessStatus: RagStatus | null;
  customerReadinessNotes: string | null;
  deliveryStatus: RagStatus | null;
  deliveryNotes: string | null;
  percentValueScoped: number;
  daysScoped: number;
  daysRemaining: number;
  valueRemaining: number;
  linkedProjectId: string | null;
  notes: string | null;
  nextStepsActions: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // New fields for projects as children
  projects?: ProjectSummary[];
  totalValue?: number; // Calculated from projects (same as scopedValue)
  scopedValue?: number; // Sum of project totalCost where totalCost > 0
  unscopedValue?: number; // servicesValue - scopedValue
  totalDays?: number; // Sum of project totalAdjustedDays
  projectCount?: number;
  overallStage?: ScopingStage | null; // Earliest stage among projects
  workstreamCoverage?: Array<{
    workstreamId: string;
    capabilityCount: number;
  }>;
}

export interface ScopingStageInfo {
  id: string;
  opportunityId: string;
  stageId: ScopingStage;
  status: 'not_started' | 'in_progress' | 'completed';
  targetDate: string | null;
  completedDate: string | null;
  confidence: number | null;
  notes: string | null;
}

export interface OpportunityActivity {
  id: string;
  opportunityId: string;
  type: 'call' | 'meeting' | 'email' | 'note';
  description: string;
  createdBy: string | null;
  createdAt: string;
}

export interface PipelineSummary {
  pipeline: {
    byStage: Record<string, { count: number; value: number }>;
    totalCount: number;
    totalValue: number;
    totalScopedValue: number;
    totalUnscopedValue: number;
  };
  inDelivery: {
    count: number;
    totalValue: number;
  };
}

export const SCOPING_STAGES: Array<{ id: ScopingStage; name: string; color: string }> = [
  { id: 'partner_briefing', name: 'Partner Briefing', color: 'bg-gray-100' },
  { id: 'services_positioned', name: 'Services Positioned', color: 'bg-blue-100' },
  { id: 'scoping_workshop', name: 'Scoping Workshop', color: 'bg-yellow-100' },
  { id: 'customer_review', name: 'Customer Review', color: 'bg-orange-100' },
  { id: 'customer_approval', name: 'Customer Approval', color: 'bg-purple-100' },
  { id: 'po_sent', name: 'PO Sent', color: 'bg-green-100' },
  { id: 'in_delivery', name: 'In Delivery', color: 'bg-emerald-100' },
];

// List opportunities
export function useOpportunities(params?: {
  search?: string;
  status?: 'pipeline' | 'in_delivery';
  stage?: ScopingStage;
  customerId?: string;
  accountDirectorId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['opportunities', params],
    queryFn: async () => {
      const queryParams: Record<string, string | number | boolean | undefined> = {};
      if (params?.search) queryParams.search = params.search;
      if (params?.status) queryParams.status = params.status;
      if (params?.stage) queryParams.stage = params.stage;
      if (params?.customerId) queryParams.customerId = params.customerId;
      if (params?.accountDirectorId) queryParams.accountDirectorId = params.accountDirectorId;
      if (params?.limit) queryParams.limit = params.limit;
      if (params?.offset) queryParams.offset = params.offset;

      return api.get<{
        opportunities: Opportunity[];
        total: number;
        limit: number;
        offset: number;
      }>('/opportunities', queryParams);
    },
  });
}

// Pipeline summary
export function usePipelineSummary() {
  return useQuery({
    queryKey: ['opportunities', 'summary'],
    queryFn: () => api.get<PipelineSummary>('/opportunities/summary'),
  });
}

// Get single opportunity
export function useOpportunity(id: string) {
  return useQuery({
    queryKey: ['opportunities', id],
    queryFn: () =>
      api.get<{
        opportunity: Opportunity & {
          projects: ProjectSummary[];
          totalValue: number;
          scopedValue: number;
          unscopedValue: number;
          totalDays: number;
          projectCount: number;
          overallStage: ScopingStage | null;
        };
        scopingStages: ScopingStageInfo[];
        mobilisationStages: ScopingStageInfo[];
        activities: OpportunityActivity[];
        // Deprecated - kept for backward compatibility
        linkedProject: {
          id: string;
          name: string;
          status: string;
          totalDays: number;
          totalCost: number;
        } | null;
      }>(`/opportunities/${id}`),
    enabled: !!id,
  });
}

// Create opportunity
export function useCreateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      customerId: string; // Required - customer-first architecture
      title: string;
      description?: string;
      salesOrg?: string;
      accountDirectorId?: string;
      accountDirectorName?: string;
      servicesValue?: number;
      currency?: 'GBP' | 'USD' | 'EUR';
      funding?: string;
      fundingTypeId?: string;
      serviceClass?: string;
      contractStartDate?: string;
      servicesExpiryDate?: string;
      notes?: string;
    }) => api.post<{ opportunity: Opportunity }>('/opportunities', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

// Update opportunity
export function useUpdateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      customerName?: string;
      title?: string;
      description?: string | null;
      salesOrg?: string | null;
      accountDirectorId?: string | null;
      accountDirectorName?: string | null;
      scopingLeadId?: string | null;
      scopingLeadName?: string | null;
      deliveryLeadId?: string | null;
      deliveryLeadName?: string | null;
      engagementStatus?: 'pipeline' | 'in_delivery';
      currentScopingStage?: ScopingStage;
      servicesValue?: number | null;
      currency?: 'GBP' | 'USD' | 'EUR';
      funding?: string | null;
      fundingTypeId?: string | null;
      serviceClass?: string | null;
      contractStartDate?: string | null;
      servicesExpiryDate?: string | null;
      customerRelationshipStatus?: RagStatus | null;
      customerRelationshipNotes?: string | null;
      scopingStatus?: RagStatus | null;
      scopingNotes?: string | null;
      customerReadinessStatus?: RagStatus | null;
      customerReadinessNotes?: string | null;
      deliveryStatus?: RagStatus | null;
      deliveryNotes?: string | null;
      notes?: string | null;
      nextStepsActions?: string | null;
      linkedProjectId?: string | null;
    }) => api.patch<{ opportunity: Opportunity }>(`/opportunities/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.id] });
    },
  });
}

// Update scoping stage
export function useUpdateScopingStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      opportunityId,
      stageId,
      ...data
    }: {
      opportunityId: string;
      stageId: ScopingStage;
      status: 'not_started' | 'in_progress' | 'completed';
      targetDate?: string | null;
      completedDate?: string | null;
      confidence?: number | null;
      notes?: string | null;
    }) =>
      api.put<{ stage: ScopingStageInfo }>(
        `/opportunities/${opportunityId}/scoping-stages/${stageId}`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.opportunityId] });
    },
  });
}

// Add activity
export function useAddOpportunityActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      opportunityId,
      ...data
    }: {
      opportunityId: string;
      type: 'call' | 'meeting' | 'email' | 'note';
      description: string;
    }) =>
      api.post<{ activity: OpportunityActivity }>(
        `/opportunities/${opportunityId}/activities`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.opportunityId] });
    },
  });
}

// Link project
export function useLinkProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      opportunityId,
      projectId,
    }: {
      opportunityId: string;
      projectId: string;
    }) =>
      api.post<{ opportunity: Opportunity }>(
        `/opportunities/${opportunityId}/link-project`,
        { projectId }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.opportunityId] });
    },
  });
}

// Delete opportunity
export function useDeleteOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/opportunities/${id}`),
    onSuccess: (_, id) => {
      // Skip the deleted opportunity's detail query to avoid a 404 refetch while
      // OpportunityDetail is still mounted. See useDeleteProject for the same pattern.
      queryClient.invalidateQueries({
        queryKey: ['opportunities'],
        predicate: (query) => query.queryKey[1] !== id,
      });
    },
  });
}

// Create project under opportunity
export function useCreateProjectUnderOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      opportunityId,
      ...data
    }: {
      opportunityId: string;
      name: string;
      description?: string;
      deliveryLocation?: 'Remote' | 'Onsite' | 'Hybrid' | 'Manual';
      engagementStartDate?: string;
      daysPerWeek?: number;
    }) =>
      api.post<{ project: ProjectSummary }>(
        `/opportunities/${opportunityId}/projects`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
